/**
 * InputTypeBadge - compact indicator of an audit's input source: a scraped
 * website vs. a document (file upload or pasted text). Mirrors LanguageBadge's
 * visual pattern (inline SVG glyph + pill + translated accessible label).
 */

import { useTranslation } from "react-i18next";
import type { AuditInputType } from "@/types";

interface InputTypeBadgeProps {
  inputType: AuditInputType;
  className?: string;
}

export function InputTypeBadge(
  { inputType, className = "" }: InputTypeBadgeProps,
) {
  const { t } = useTranslation(["history"]);
  const label = t(`history:inputType.${inputType}`);

  return (
    <span
      data-test="audit-input-type-badge"
      data-input-type={inputType}
      title={label}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--secondary)] px-2 py-0.5 text-xs font-medium text-[var(--muted-foreground)] ${className}`}
    >
      {inputType === "website"
        ? (
          <svg
            aria-hidden="true"
            className="h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <circle cx="12" cy="12" r="9" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18"
            />
          </svg>
        )
        : (
          <svg
            aria-hidden="true"
            className="h-3 w-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 3h7l5 5v13a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5" />
          </svg>
        )}
      {label}
    </span>
  );
}
